import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RecordContextProvider, useGetOne, usePermissions, useTranslate } from 'react-admin';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useDrawer } from '@/composables/useDrawer';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import { canViewDetails } from '@/helpers/permissions/canViewDetails.js';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { CustomButton } from '@/components/global/buttons/CustomButton';
import CollapsibleSection from '@/components/global/fields/CollapsibleSection';
import EditIcon from '@/assets/icons/edit-icon.svg?react';
import { BrandBasicDetails, BrandContractorsDetails } from '@/components/brand/BrandDetails.jsx';
import { brandHasConfigurationOverride } from '@/components/brand/validators/brandValidators.js';
import { RetailChainConfigurationDetails } from '@/components/retailChain/RetailChainDetails.jsx';

export const BrandShow = ({ handleClose, elementId }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();
    const { closeAllDrawers, openDrawer } = useDrawer();
    const allowed = canViewDetails('brand', permissions);
    const { data, isPending, error } = useGetOne('brand', { id: elementId }, { enabled: Boolean(elementId) && allowed && !isPermissionsLoading });
    const [isBasicOpen, setIsBasicOpen] = useState(true);
    const [isConfigurationOpen, setIsConfigurationOpen] = useState(false);
    const [isContractorsOpen, setIsContractorsOpen] = useState(false);
    const canEdit = Boolean(permissions?.isAdmin);
    const showConfiguration = brandHasConfigurationOverride(data);
    const loadingState = getLoadingState({ isLoading: isPending, error, data, translate });

    useEffect(() => {
        if (!isPermissionsLoading && !allowed) {
            closeAllDrawers();
        }
    }, [allowed, closeAllDrawers, isPermissionsLoading]);

    if (isPermissionsLoading || !allowed) {
        return null;
    }

    return (
        <Box>
            <Box className='popup-wrapper popup-wrapper-top'>
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{data?.name || translate('resources.brand.labels.details')}</Typography>
                    <Box className='popup-box__more-actions-grey' sx={{ height: '44px' }}>
                        {canEdit && data?.id ? (
                            <CustomButton
                                color='primary'
                                label='common.edit'
                                variant='contained'
                                size={isDesktop ? 'large' : 'small'}
                                startIcon={<EditIcon />}
                                onClick={() => openDrawer(elementId, 'brand-edit')}
                            />
                        ) : null}
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            {loadingState || (
                <Box className='popup-box'>
                    <RecordContextProvider value={data}>
                        <CollapsibleSection
                            title={translate('resources.brand.labels.basicInfo')}
                            isOpen={isBasicOpen}
                            onToggle={() => setIsBasicOpen(open => !open)}
                        >
                            <BrandBasicDetails />
                        </CollapsibleSection>
                        {showConfiguration ? (
                            <CollapsibleSection
                                title={translate('resources.brand.labels.configuration')}
                                isOpen={isConfigurationOpen}
                                onToggle={() => setIsConfigurationOpen(open => !open)}
                            >
                                <RetailChainConfigurationDetails />
                            </CollapsibleSection>
                        ) : null}
                        <CollapsibleSection
                            title={translate('menu.contractors')}
                            isOpen={isContractorsOpen}
                            onToggle={() => setIsContractorsOpen(open => !open)}
                        >
                            <BrandContractorsDetails />
                        </CollapsibleSection>
                    </RecordContextProvider>
                </Box>
            )}
        </Box>
    );
};

BrandShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
